import React, { useState, useEffect } from 'react';
import { postAPI } from '../../../app/api';
import Swal from 'sweetalert2';

const MailLog = () => {
    const [emailHistory, setEmailHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedEmail, setExpandedEmail] = useState(null);
    const [emailRecipients, setEmailRecipients] = useState({});
    const [loadingRecipients, setLoadingRecipients] = useState({});

    useEffect(() => {
        fetchEmailHistory();
    }, []);

    const fetchEmailHistory = async () => {
        try {
            setLoading(true);
            const response = await postAPI('email/history', {});
            if (response?.status === 1) {
                setEmailHistory(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching email history:', error);
            Swal.fire('Error', 'Failed to fetch email history', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchEmailRecipients = async (emailId) => {
        try {
            setLoadingRecipients(prev => ({ ...prev, [emailId]: true }));
            const response = await postAPI('email/history/details', { id: emailId });
            if (response?.status === 1) {
                setEmailRecipients(prev => ({ ...prev, [emailId]: response?.data || [] }));
            }
        } catch (error) {
            console.error('Error fetching email recipients:', error);
            Swal.fire('Error', 'Failed to fetch recipients', 'error');
        } finally {
            setLoadingRecipients(prev => ({ ...prev, [emailId]: false }));
        }
    };

    const toggleRecipients = async (emailId) => {
        if (expandedEmail === emailId) {
            setExpandedEmail(null);
        } else {
            setExpandedEmail(emailId);
            if (!emailRecipients[emailId]) {
                await fetchEmailRecipients(emailId);
            }
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'completed': { class: 'badge-success', text: 'Completed' },
            'processing': { class: 'badge-warning', text: 'Processing' },
            'failed': { class: 'badge-danger', text: 'Failed' },
            'pending': { class: 'badge-secondary', text: 'Pending' },
            'sent': { class: 'badge-success', text: 'Sent' }
        };
        const config = statusConfig[status] || { class: 'badge-secondary', text: status };
        return <span className={`badge ${config.class}`}>{config.text}</span>;
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading email history...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-body">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Email History</h5>
                    <button className="btn btn-sm btn-primary" onClick={fetchEmailHistory}>
                        <i className="bi bi-arrow-clockwise me-1"></i> Refresh
                    </button>
                </div>
                {emailHistory.length === 0 ? (
                    <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        No email history found. Send your first email to see it here.
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-hover cus-table">
                            <thead>
                                <tr>
                                    <th>Date & Time</th>
                                    <th>Group</th>
                                    <th>Subject</th>
                                    <th>Recipients</th>
                                    <th>Sent</th>
                                    <th>Failed</th>
                                    <th>Status</th>
                                    <th>Sent By</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {emailHistory.map((email) => (
                                    <React.Fragment key={email.id}>
                                        <tr>
                                            <td>{formatDateTime(email.sent_at)}</td>
                                            <td>{email.group_name || 'N/A'}</td>
                                            <td>
                                                <div className="text-truncate" style={{ maxWidth: '200px' }} title={email.subject}>
                                                    {email.subject}
                                                </div>
                                            </td>
                                            <td>{email.recipients_count}</td>
                                            <td>
                                                <span className="badge badge-success">{email.sent_count || 0}</span>
                                            </td>
                                            <td>
                                                <span className="badge badge-danger">{email.failed_count || 0}</span>
                                            </td>
                                            <td>{getStatusBadge(email.status)}</td>
                                            <td>{email.sent_by || 'N/A'}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => toggleRecipients(email.id)}
                                                >
                                                    {expandedEmail === email.id ? (
                                                        <>
                                                            <i className="bi bi-chevron-up"></i> Hide
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-chevron-down"></i> View
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedEmail === email.id && (
                                            <tr>
                                                <td colSpan="9" className="bg-light">
                                                    <div className="p-3">
                                                        <h6>Message:</h6>
                                                        <div className="mb-3 p-2 bg-white border rounded">
                                                            {email.message}
                                                        </div>
                                                        <h6>Recipients Details:</h6>
                                                        {loadingRecipients[email.id] ? (
                                                            <div className="text-center py-3">
                                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            </div>
                                                        ) : emailRecipients[email.id]?.length > 0 ? (
                                                            <div className="table-responsive">
                                                                <table className="table table-sm table-bordered cus-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Recipient Name</th>
                                                                            <th>Email</th>
                                                                            <th>Status</th>
                                                                            <th>Sent At</th>
                                                                            <th>Error Message</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {emailRecipients[email.id].map((recipient, idx) => (
                                                                            <tr key={idx}>
                                                                                <td>{recipient.recipient_name}</td>
                                                                                <td>{recipient.recipient_email}</td>
                                                                                <td>{getStatusBadge(recipient.status)}</td>
                                                                                <td>{formatDateTime(recipient.sent_at)}</td>
                                                                                <td>
                                                                                    {recipient.error_message ? (
                                                                                        <span className="text-danger small">
                                                                                            {recipient.error_message}
                                                                                        </span>
                                                                                    ) : (
                                                                                        '-'
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <p className="text-muted">No recipient details available</p>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MailLog;
